import { Alert, Button, Field, Input } from '@personachat/ui';
import { useState, type FormEvent } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';

import { useSession } from '../auth/AuthProvider';
import { getSessionState, login, logout, terminerEnrolement, verifierDoubleAuth } from '../auth/session';
import { Enrolement, FormulaireCode } from '../features/security/DoubleAuth';

export function LoginPage() {
  const { status, notice } = useSession();
  const navigate = useNavigate();
  const location = useLocation();
  const destination = (location.state as { from?: string } | null)?.from ?? '/';

  const [email, setEmail] = useState('');
  const [motDePasse, setMotDePasse] = useState('');
  const [erreur, setErreur] = useState<string | null>(null);
  const [envoi, setEnvoi] = useState(false);

  if (status === 'authenticated') return <Navigate to={destination} replace />;
  if (status === 'second-factor' || status === 'enrollment') return <EtapeDoubleAuth />;

  const soumettre = async (e: FormEvent) => {
    e.preventDefault();
    if (envoi) return;
    setEnvoi(true);
    setErreur(null);
    try {
      await login(email, motDePasse);
      // Double authentification : l'écran passe à l'étape du code (état de session).
      if (getSessionState().status === 'authenticated') navigate(destination, { replace: true });
      else setEnvoi(false);
    } catch (err) {
      setErreur(err instanceof Error && err.message ? err.message : 'Connexion impossible. Réessayez.');
      setEnvoi(false);
    }
  };

  return (
    <div className="login">
      <form className="login__card" onSubmit={soumettre} noValidate>
        <Marque />

        {notice && !erreur && <Alert tone="info">{notice}</Alert>}
        {erreur && <Alert>{erreur}</Alert>}

        <Field id="email" label="E-mail">
          <Input
            id="email"
            type="email"
            autoComplete="username"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoFocus
          />
        </Field>
        <Field id="password" label="Mot de passe">
          <Input
            id="password"
            type="password"
            autoComplete="current-password"
            required
            value={motDePasse}
            onChange={(e) => setMotDePasse(e.target.value)}
          />
        </Field>
        <Button type="submit" variant="primary" block loading={envoi} disabled={!email || !motDePasse}>
          Se connecter
        </Button>
      </form>
    </div>
  );
}

function Marque({ sousTitre = 'MyBizGame IA · réservé à l’équipe' }: { sousTitre?: string }) {
  return (
    <div className="login__brand">
      <span className="sidebar__logo" aria-hidden="true">
        M
      </span>
      <div>
        <h1>Back-office</h1>
        <p>{sousTitre}</p>
      </div>
    </div>
  );
}

/**
 * Après le mot de passe (cahier des charges §9) : code de l'application, ou
 * activation de la double authentification quand elle est obligatoire.
 */
function EtapeDoubleAuth() {
  const { status, user, notice } = useSession();
  const [secours, setSecours] = useState(false);
  return (
    <div className="login">
      <div className="login__card">
        <Marque sousTitre={user?.email ?? 'MyBizGame IA · réservé à l’équipe'} />
        {status === 'second-factor' ? (
          <>
            <h2 className="login__etape">Double authentification</h2>
            {notice && <Alert tone="info">{notice}</Alert>}
            <FormulaireCode
              key={secours ? 'secours' : 'totp'}
              libelle={secours ? 'Code de secours' : 'Code à 6 chiffres'}
              aide={secours ? 'Format XXXX-XXXX. Chaque code de secours ne sert qu’une fois.' : 'Ouvrez votre application d’authentification.'}
              bouton="Valider"
              onValider={verifierDoubleAuth}
            />
            <button type="button" className="lien-discret" onClick={() => setSecours((s) => !s)}>
              {secours ? 'Utiliser le code de l’application' : 'Téléphone perdu ? Utiliser un code de secours'}
            </button>
          </>
        ) : (
          <>
            <h2 className="login__etape">Activez la double authentification</h2>
            <Alert tone="info">Elle est obligatoire pour les super admins : un code du téléphone sera demandé à chaque connexion.</Alert>
            <Enrolement onTermine={terminerEnrolement} />
          </>
        )}
        <button type="button" className="lien-discret" onClick={() => logout()}>
          Se déconnecter
        </button>
      </div>
    </div>
  );
}
